<template>
  <div class="prognose-header">
    <!-- Main Header Row -->
    <div class="header-main-row">
      <!-- Left: Dropdown Menus -->
      <div class="header-left">
        <div class="d-flex align-items-center gap-2">
          <!-- Save Dialog -->
          <opslaan-dialog
            :inspectie="inspectie"
            :unit="unit"
            :on-update="onUpdate"
            :on-delete="onDelete"
          />

          <!-- View Settings Dropdown -->
          <b-dropdown 
            variant="outline-secondary"
            text="Weergave"
            class="view-dropdown"
          >
            <template #button-content>
              <i class="fa fa-eye me-1" />
              <span class="d-none d-md-inline">Weergave</span>
            </template>
            
            <b-dropdown-header>Weergave modus</b-dropdown-header>
            <b-dropdown-item-button
              v-for="mode in modi"
              :key="mode.value"
              :active="inspectie.inspectie.config && inspectie.inspectie.config.modus === mode.value"
              @click="inspectie.inspectie.config.modus = mode.value"
            >
              {{ mode.text }}
            </b-dropdown-item-button>
            
            <b-dropdown-divider />
            <b-dropdown-header>Panelen</b-dropdown-header>
            
            <b-dropdown-item-button
              :active="showTechnischeDataInfo"
              @click="$emit('update:showTechnischeDataInfo', !showTechnischeDataInfo)"
            >
              <i class="fa fa-info-circle me-2" />
              Technische Data
              <b-badge 
                v-if="nogTeChecken.length > 0" 
                variant="danger" 
                pill
                class="ms-2"
              >
                {{ nogTeChecken.length }}
              </b-badge>
            </b-dropdown-item-button>
            
            <b-dropdown-item-button
              :active="inspectie.inspectie.config && inspectie.inspectie.config.showModeratorPanel"
              @click="inspectie.inspectie.config.showModeratorPanel = !inspectie.inspectie.config.showModeratorPanel"
            >
              <i class="fa fa-magnet me-2" />
              Moderator Panel
            </b-dropdown-item-button>
            
            <b-dropdown-item-button
              :active="inspectie.inspectie.config && inspectie.inspectie.config.showHeaderRows"
              @click="inspectie.inspectie.config.showHeaderRows = !inspectie.inspectie.config.showHeaderRows"
            >
              <i class="fa fa-list me-2" />
              Header Rijen
            </b-dropdown-item-button>
          </b-dropdown>

          <!-- Period Settings Dropdown -->
          <b-dropdown 
            variant="outline-secondary"
            class="period-dropdown"
          >
            <template #button-content>
              <i class="fa fa-calendar me-1" />
              <span class="d-none d-lg-inline">{{ (inspectie.inspectie.config && inspectie.inspectie.config.jaar) || 2024 }}</span>
            </template>
            
            <b-dropdown-header>Periode instellingen</b-dropdown-header>
            <div class="dropdown-form p-2">
              <div class="mb-2">
                <label class="form-label small">Start jaar</label>
                <b-input
                  v-model.number="inspectie.inspectie.config.jaar"
                  type="number"
                  size="sm"
                  style="width: 80px;"
                />
              </div>
              <div>
                <label class="form-label small">Periode</label>
                <b-form-select
                  v-model="inspectie.inspectie.config.jaren"
                  :options="jarenOpties"
                  size="sm"
                  style="width: 80px;"
                />
              </div>
            </div>
          </b-dropdown>

          <!-- Actions Dropdown -->
          <b-dropdown 
            variant="outline-secondary"
            class="actions-dropdown"
          >
            <template #button-content>
              <i class="fa fa-cogs me-1" />
              <span class="d-none d-md-inline">Acties</span>
            </template>
            
            <b-dropdown-item-button
              v-if="inspectie.inspectie.config && inspectie.inspectie.config.modus === 'Positie'"
              @click="inspectie.inspectie.onderdelen.push({ title: '', jaar: null })"
            >
              <i class="fa fa-plus me-2" />
              Nieuw Element
            </b-dropdown-item-button>
            
            <b-dropdown-item-button
              @click="$emit('show-transpose')"
            >
              <i class="fa fa-arrows-alt-h me-2" />
              Verschuif Uitvoeringen
            </b-dropdown-item-button>
          </b-dropdown>
        </div>
      </div>

      <!-- Center: Condition Slider -->
      <div class="header-center" v-if="inspectie.inspectie.config">
        <div class="condition-inline">
          <small class="text-muted me-2">
            {{ new Date().getFullYear() + (inspectie.inspectie.config.inclusief || 0) }}
          </small>
          <b-input 
            v-model.number="inspectie.inspectie.config.inclusief" 
            type="range"
            size="sm"
            min="0" 
            max="10"
            class="condition-slider"
          />
          <conditie-limit
            :value="maxConditieValue"
            @input="$emit('update:maxConditieValue', $event)"
            :show-info="false"
            scope="global"
            class="ms-2"
          />
        </div>
      </div>

      <!-- Right: Prognose Info Card -->
      <div class="header-right">
        <div class="prognose-info-card">
          <div class="prognose-title">
            <h5 class="mb-1">
              <i 
                v-if="inspectie.isBevroren" 
                class="fa fa-lock me-2 text-warning"
              />
              {{ inspectie.omschrijving || 'Prognose' }}
            </h5>
            <div class="prognose-meta">
              <span class="inspection-date me-3">
                <i class="fa fa-calendar me-1" />
                {{ $options.filters.formatDateTime(inspectie.datum, 'DD-MM-YYYY') }}
              </span>
              <span class="inspector-name">
                <i class="fa fa-user me-1" />
                {{ inspectie.inspecteur ? inspectie.inspecteur.naam : "Geen inspecteur" }}
              </span>
            </div>
          </div>
          
          <div class="header-badges mt-2">
            <b-badge 
              v-if="inspectie.isTemplate" 
              variant="info"
              class="me-1"
            >
              Template
            </b-badge>
            <b-badge 
              v-if="prognoseid == unit.HuidigePrognose" 
              variant="warning"
              class="me-1"
            >
              Huidige
            </b-badge>
            <b-badge 
              v-if="inspectie.gereed" 
              variant="success"
              class="me-1"
            >
              <i class="fa fa-check me-1" /> Voltooid
            </b-badge>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ConditieLimit from './ConditieLimit.vue';
import OpslaanDialog from '../views/prognoses/OpslaanDialog.vue';

export default {
  name: 'PrognoseHeader',
  components: {
    ConditieLimit,
    OpslaanDialog,
  },
  props: {
    inspectie: { type: Object, required: true },
    unit: { type: Object, required: true },
    prognoseid: { type: String, required: true },
    nogTeChecken: { type: Array, default: () => [] },
    onUpdate: { type: Function, required: true },
    onDelete: { type: Function, required: true },
    maxConditieValue: { type: Number, default: null },
    showTechnischeDataInfo: { type: Boolean, default: false },
  },
  data() {
    return {
      jarenOpties: [
        { value: 5, text: '5 jaar' },
        { value: 10, text: '10 jaar' },
        { value: 15, text: '15 jaar' },
        { value: 20, text: '20 jaar' },
      ],
      modi: [
        { text: 'Detail', value: 'Detail' },
        { text: 'Positie', value: 'Positie' },
      ],
    };
  },
};
</script>

<style scoped>
.prognose-header {
  background: var(--bs-gray-800, #343a40);
  border-radius: 0.375rem;
  padding: 1rem;
  margin-bottom: 1rem;
  border: 1px solid var(--bs-gray-700, #495057);
  color: var(--bs-light, #f8f9fa);
}

.header-main-row {
  display: grid;
  grid-template-columns: auto auto 1fr;
  gap: 1.5rem;
  align-items: center;
  min-height: 60px;
}

.header-left {
  display: flex;
  align-items: center;
}

.header-center {
  display: flex;
  justify-content: center;
  align-items: center;
}

.condition-inline {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: var(--bs-gray-700, #495057);
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  min-width: 300px;
}

.condition-slider {
  min-width: 120px;
  flex: 1;
}

.header-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.prognose-info-card {
  background: linear-gradient(135deg, var(--bs-gray-700, #495057) 0%, var(--bs-gray-800, #343a40) 100%);
  border-radius: 0.5rem;
  padding: 1rem 1.25rem;
  border-left: 4px solid var(--bs-primary, #0d6efd);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  min-width: 300px;
  backdrop-filter: blur(10px);
}

.prognose-title h5 {
  color: var(--bs-light, #f8f9fa);
  margin: 0;
  font-weight: 600;
  font-size: 1.1rem;
  line-height: 1.3;
}

.prognose-meta {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 0.25rem;
  flex-wrap: wrap;
}

.inspection-date,
.inspector-name {
  color: var(--bs-gray-300, #adb5bd);
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
}

.inspection-date i,
.inspector-name i {
  color: var(--bs-primary, #0d6efd);
  width: 16px;
  text-align: center;
  font-size: 0.875rem;
}

.header-badges {
  display: flex;
  gap: 0.375rem;
  flex-wrap: wrap;
  justify-content: flex-start;
}

.form-label {
  font-weight: 500;
  margin-bottom: 0.25rem;
  color: var(--bs-gray-300, #adb5bd);
}

/* Dropdown form styling */
.dropdown-form {
  min-width: 180px;
  background: var(--bs-gray-800, #343a40);
}

.dropdown-form .form-control,
.dropdown-form .form-select {
  background-color: var(--bs-gray-700, #495057);
  border-color: var(--bs-gray-600, #6c757d);
  color: var(--bs-light, #f8f9fa);
}

.dropdown-form .form-control:focus,
.dropdown-form .form-select:focus {
  background-color: var(--bs-gray-700, #495057);
  border-color: var(--bs-primary, #0d6efd);
  color: var(--bs-light, #f8f9fa);
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

/* Range input styling */
input[type="range"] {
  background: transparent;
  -webkit-appearance: none;
  appearance: none;
  height: 6px;
  border-radius: 3px;
  background: var(--bs-gray-600, #6c757d);
  outline: none;
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--bs-primary, #0d6efd);
  cursor: pointer;
  border: 2px solid var(--bs-light, #f8f9fa);
}

input[type="range"]::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--bs-primary, #0d6efd);
  cursor: pointer;
  border: 2px solid var(--bs-light, #f8f9fa);
}

/* Dropdown button styling */
.btn-outline-secondary {
  border-color: var(--bs-gray-600, #6c757d);
  color: var(--bs-gray-300, #adb5bd);
  min-height: 38px;
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
}

.btn-outline-secondary:hover {
  background-color: var(--bs-gray-600, #6c757d);
  border-color: var(--bs-gray-600, #6c757d);
  color: var(--bs-light, #f8f9fa);
}

/* Ensure all dropdown buttons have consistent sizing */
.header-left .dropdown .btn {
  min-height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .header-main-row {
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto;
    gap: 1rem;
  }
  
  .header-center {
    grid-column: 1 / -1;
    justify-content: flex-start;
    order: 3;
  }
  
  .condition-inline {
    min-width: unset;
    width: 100%;
  }
  
  .prognose-info-card {
    min-width: 250px;
  }
  
  .prognose-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}

@media (max-width: 768px) {
  .header-main-row {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
  
  .header-left {
    order: 1;
  }
  
  .header-right {
    order: 2;
  }
  
  .header-center {
    order: 3;
  }
  
  .header-left .d-flex {
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  
  .header-right {
    justify-content: stretch;
  }
  
  .prognose-info-card {
    min-width: unset;
    width: 100%;
  }
  
  .prognose-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .condition-inline {
    padding: 0.375rem 0.75rem;
  }
}

@media (max-width: 576px) {
  .prognose-header {
    padding: 0.75rem;
  }
  
  .header-badges {
    margin-top: 0.5rem;
  }
  
  .condition-inline {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
    text-align: center;
  }
  
  .condition-slider {
    min-width: unset;
  }
  
  .header-left .d-flex {
    justify-content: center;
  }
  
  .prognose-info-card {
    padding: 0.75rem 1rem;
    min-width: unset;
  }
  
  .prognose-title h5 {
    font-size: 1rem;
  }
  
  .prognose-meta {
    gap: 0.75rem;
  }
  
  .inspection-date,
  .inspector-name {
    font-size: 0.8125rem;
  }
}

/* Gap utilities for older Bootstrap versions */
.gap-1 > * + * {
  margin-left: 0.25rem;
}

.gap-2 > * + * {
  margin-left: 0.5rem;
}
</style>
  .header-title-row h6 {
    font-size: 0.9rem;
  }
  
  .header-title-row small {
    font-size: 0.75rem;
  }
  
  .condition-controls {
    min-width: unset;
  }
}

@media (max-width: 576px) {
  .header-title-row {
    flex-direction: column;
    align-items: stretch !important;
  }
  
  .header-title-row h6 {
    margin-bottom: 0.5rem;
  }
  
  .header-controls-row .d-flex {
    justify-content: space-between;
  }
  
  .settings-card .row > * {
    margin-bottom: 0.5rem;
  }
}

/* Text truncation utility */
.min-width-0 {
  min-width: 0;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Gap utilities for older Bootstrap versions */
.gap-1 > * + * {
  margin-left: 0.25rem;
}

.gap-2 > * + * {
  margin-left: 0.5rem;
}
</style>